/**
 * mmc_arcade_system.js
 * Specialized shell for Madmoco Arcade Games
 */

import React, { useState, useEffect, Suspense } from 'react';
import { List, X, House } from '@phosphor-icons/react';
import MmcNavCategories from './mmc_nav_categories';
import MmcGlobalSearch from './mmc_global_search';
import MmcCookieGuardian from './mmc_cookie_guardian';
import MmcLegalModal from './mmc_legal_modal';
import MmcDashboard from './mmc_dashboard';
import MmcArcadeCabinet from './mmc_arcade_cabinet';
import logo from '../assets/img/mmc_logo_orange_360x90.png';
import '../styles/mmc-tab-system.css';
import '../styles/mmc-nav-categories.css';
import '../styles/mmc-arcade-core.css';

import { MmcStack } from './ui';

const MmcArcadeSystem = ({ games = [], registry = null }) => {
  const [active_tab, set_active_tab] = useState(null); // Tab is the game ID
  const [mobile_menu_open, set_mobile_menu_open] = useState(false);
  const [available_games, set_available_games] = useState([]);
  const [legal_modal, set_legal_modal] = useState({ open: false, type: null });

  useEffect(() => {
    try {
      if (window && window.innerWidth >= 1024) {
        set_mobile_menu_open(true);
      }
    } catch (e) { }

    const enabled = games
      .filter(m => m.enabled !== false)
      .sort((a, b) => (a.order || 999) - (b.order || 999));

    set_available_games(enabled);
  }, [games, active_tab, registry]);

  const handle_logo_click = () => {
    set_active_tab(null);
    if (window.innerWidth < 1024) set_mobile_menu_open(false);
  };

  useEffect(() => {
    const active = available_games.find(m => m.id === active_tab);
    if (active) {
      document.title = `${active.display_name} | Madmoco Arcade`;
    } else {
      document.title = `Madmoco Arcade - Retro Gaming System`;
    }
  }, [active_tab, available_games]);

  const handle_game_click = (game_id) => {
    set_active_tab(game_id);
    if (window.innerWidth < 1024) set_mobile_menu_open(false);
    const mainContent = document.getElementById('mmc-main-content');
    if (mainContent) mainContent.focus();
  };

  const active_game = available_games.find(m => m.id === active_tab);

  const get_component = (game_id) => {
    if (registry) {
      const comp = registry.get_component(game_id);
      if (comp) return comp;
    }
    const manifest = available_games.find(m => m.id === game_id);
    return manifest?.component || null;
  };

  const ActiveComponent = active_game ? get_component(active_game.id) : null;

  return (
    <div className="mmc-tab-system">
      <a href="#mmc-main-content" className="mmc-skip-link">Skip to game content</a>

      <MmcGlobalSearch games={available_games} onGameSelect={handle_game_click} />

      {/* Mobile Header */}
      <header className="mmc-tab-system__mobile-header">
        <div className="mmc-tab-system__mobile-logo" onClick={handle_logo_click} style={{ cursor: 'pointer' }}>
          <img src={logo} alt="Madmoco Arcade" className="mmc-logo-mobile" width="360" height="90" />
        </div>
        <button
          className="mmc-tab-system__menu-toggle"
          onClick={() => set_mobile_menu_open(!mobile_menu_open)}
          aria-label={mobile_menu_open ? "Close menu" : "Open menu"}
        >
          {mobile_menu_open ? <X size={24} weight="bold" /> : <List size={24} weight="bold" />}
        </button>
      </header>

      {/* Navigation Tray */}
      <nav className={`mmc-tab-system__nav ${mobile_menu_open ? 'mmc-tab-system__nav--open' : ''}`} aria-label="Game Library">
        <div className="mmc-tab-system__nav-header" onClick={handle_logo_click} style={{ cursor: 'pointer' }}>
          <img src={logo} alt="Madmoco Logo" className="mmc-logo-nav" width="360" height="90" />
        </div>

        <MmcNavCategories
          games={available_games}
          onGameSelect={handle_game_click}
          activeTab={active_tab}
        />

        <footer className="mmc-tab-system__nav-footer">
          <div className="mmc-tab-system__footer-content">
            <div className="mmc-tab-system__footer-main">
              &copy; {new Date().getFullYear()} <a href="https://www.madmoco.com" className="mmc-tab-system__footer-link">MADMOCO</a>.ARCADE
            </div>
            <div className="mmc-tab-system__footer-legal">
              <button onClick={() => set_legal_modal({ open: true, type: 'privacy' })} className="mmc-legal-link">PRIVACY</button>
              <button onClick={() => set_legal_modal({ open: true, type: 'cookies' })} className="mmc-legal-link">COOKIES</button>
              <button onClick={() => set_legal_modal({ open: true, type: 'terms' })} className="mmc-legal-link">TERMS</button>
            </div>
          </div>
        </footer>
      </nav>

      {/* Main Content Area */}
      <main id="mmc-main-content" className="mmc-tab-system__content" tabIndex="-1">
        {ActiveComponent ? (
          <div className="mmc-game-scene">
            <MmcStack gap={0} className="mmc-stack--fullscreen">
              <MmcArcadeCabinet
                title={active_game.display_name}
                gameId={active_game.id}
                fullScreen={true}
              >
                <Suspense fallback={
                  <div className="mmc-loader-container">
                    <div className="mmc-loader"></div>
                    <p className="ms-text-mono">LOADING_ROM...</p>
                  </div>
                }>
                  <ActiveComponent />
                </Suspense>
              </MmcArcadeCabinet>
            </MmcStack>
          </div>
        ) : (
          <MmcDashboard
            games={available_games}
            onSelectGame={handle_game_click}
          />
        )}
      </main>

      <MmcCookieGuardian />
      <MmcLegalModal
        type={legal_modal.type}
        isOpen={legal_modal.open}
        onClose={() => set_legal_modal({ open: false, type: null })}
      />

      <style>{`
        .mmc-game-scene {
            animation: sceneEnter 0.6s cubic-bezier(0.16, 1, 0.3, 1);
            display: flex;
            justify-content: center;
            align-items: center;
            height: 100%;
            width: 100%;
        }
        @keyframes sceneEnter {
            from { opacity: 0; transform: scale(0.98); }
            to { opacity: 1; transform: scale(1); }
        }
        .mmc-stack--fullscreen {
            width: 100%;
            height: 100%;
            max-width: none;
            display: flex;
            flex-direction: column;
        }
        .mmc-tab-system__content:has(.mmc-stack--fullscreen) {
            padding: 0 !important;
            overflow: hidden !important;
        }
      `}</style>
    </div>
  );
};

export default MmcArcadeSystem;
